import { Badge, Box, Flex, HStack, SimpleGrid, useToast } from '@chakra-ui/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import MiniStatistics from 'components/card/MiniStatistics';
import CustomButton from 'components/CustomButton';
import TableComponent from 'components/TableComponent';
import { queryClient } from 'index';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { FiAlertCircle, FiCalendar, FiCreditCard, FiUsers } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { Column } from 'react-table';
import { findAllRecurrencesClients, findRecurrenceClientsSummary, removeClientFromRecurrence } from 'services/api.service';
import { maskCurrency } from 'utils/number';
import AddClientWithoutRecurrence from './components/AddClientWithoutRecurrence';
import ConfirmRemoveRecurrenceClientButton from './components/ConfirmRemoveRecurrenceClientButton';
import { RecurrencyPeriodDescription } from './components/recurrency-type';
import ShowPaymentsRecurrence from './components/ShowPayments';

const getNextChargeBadge = (value?: string | null) => {
  if (!value) {
    return <Badge colorScheme="gray">Sem agenda</Badge>;
  }

  const nextCharge = DateTime.fromISO(value);

  if (nextCharge < DateTime.now().startOf('day')) {
    return <Badge colorScheme="red">{nextCharge.toFormat('dd/MM/yyyy HH:mm')}</Badge>;
  }

  if (nextCharge <= DateTime.now().plus({ days: 7 }).endOf('day')) {
    return <Badge colorScheme="yellow">{nextCharge.toFormat('dd/MM/yyyy HH:mm')}</Badge>;
  }

  return <Badge colorScheme="green">{nextCharge.toFormat('dd/MM/yyyy HH:mm')}</Badge>;
};

export default function Recurrency() {
  const navigate = useNavigate();
  const toast = useToast();
  const { mutateAsync: removeRecurrenceClient, isLoading: isRemovingRecurrenceClient } =
    useMutation(removeClientFromRecurrence);
  const { data: summary } = useQuery({
    queryKey: ['findRecurrenceClientsSummary'],
    queryFn: findRecurrenceClientsSummary,
  });

  const columns = useMemo(
    () =>
      [
        {
          Header: 'Cliente',
          accessor: 'client.name',
        },
        {
          Header: 'Plano',
          accessor: 'plan.description',
        },
        {
          Header: 'Valor',
          accessor: 'plan.amount',
          sortType: 'basic',
          Cell: ({ value }) => maskCurrency(value),
        },
        {
          Header: 'Período',
          accessor: 'period',
          Cell: ({ value }) => RecurrencyPeriodDescription[value],
        },
        {
          Header: 'Pago',
          accessor: 'totalPaid',
          Cell: ({ value }) => maskCurrency(value),
        },
        {
          Header: 'Pendente',
          accessor: 'totalPending',
          Cell: ({ value }) => {
            return +value > 0 ? <Badge colorScheme="yellow">{maskCurrency(value)}</Badge> : maskCurrency(value);
          },
        },
        {
          accessor: 'lastDate',
          Header: 'Última cobrança',
          Cell: ({ value }) => {
            return value ? DateTime.fromISO(value).toFormat('dd/MM/yyyy HH:mm:ss') : '---';
          },
        },
        {
          accessor: 'nextDate',
          Header: 'Próxima cobrança',
          Cell: ({ value }) => getNextChargeBadge(value),
        },
        {
          accessor: 'createdAt',
          Header: 'Criada em',
          Cell: ({ value }) => DateTime.fromISO(value).toFormat('dd/MM/yyyy HH:mm:ss'),
        },
        {
          Header: 'Ações',
          accessor: 'actions',
        },
      ] as Column[],
    [],
  );

  const onClose = () => {
    queryClient.refetchQueries({
      queryKey: ['findAllRecurrencesClients'],
      type: 'active',
    });
    queryClient.invalidateQueries({
      queryKey: ['findRecurrenceClientsSummary'],
    });
  };

  const handleRemoveRecurrenceClient = async (recurrenceClientId: string) => {
    try {
      await removeRecurrenceClient(recurrenceClientId);
      onClose();
      toast({
        title: 'Recorrência removida',
        description: 'O vínculo cliente x plano foi removido e as cobranças pendentes foram canceladas.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title: 'Erro ao remover recorrência',
        description: error?.response?.data?.message || 'Não foi possível remover a recorrência.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const actions = (row: any) => (
    <Flex align="center" justify="flex-start" w="100%">
      <ShowPaymentsRecurrence
        clientRecurrencyId={row.id}
        recurrencyName={row.plan.name}
        amount={row.plan.amount}
        oncloseMainModal={onClose}
      />
      <ConfirmRemoveRecurrenceClientButton
        isLoading={isRemovingRecurrenceClient}
        onConfirm={() => handleRemoveRecurrenceClient(row.id)}
      />
    </Flex>
  );

  return (
    <Box>
      <SimpleGrid mb="20px" columns={{ base: 1, md: 2, xl: 5 }} spacing="20px">
        <MiniStatistics
          name="Recorrências ativas"
          value={summary?.activeClients ?? 0}
          startContent={<FiUsers size="22px" />}
        />
        <MiniStatistics
          name="Valor pendente"
          value={maskCurrency(summary?.pendingAmount ?? 0)}
          startContent={<FiCreditCard size="22px" />}
        />
        <MiniStatistics
          name="Valor pago"
          value={maskCurrency(summary?.paidAmount ?? 0)}
          startContent={<FiCreditCard size="22px" />}
        />
        <MiniStatistics
          name="Cobram em 30 dias"
          value={summary?.upcomingClients ?? 0}
          description={`${summary?.activePlans ?? 0} planos ativos`}
          startContent={<FiCalendar size="22px" />}
        />
        <MiniStatistics
          name="Atrasadas"
          value={summary?.overdueClients ?? 0}
          description={`${summary?.plansWithoutWallet ?? 0} planos sem carteira`}
          startContent={<FiAlertCircle size="22px" />}
        />
      </SimpleGrid>

      <SimpleGrid mb="20px" columns={{ sm: 1 }} spacing={{ base: '20px', xl: '20px' }}>
        <TableComponent
          label="Recorrências"
          columnsData={columns}
          right={
            <HStack>
              <AddClientWithoutRecurrence />
              <CustomButton onClick={() => navigate('/access/recurrence-plans')} colorScheme="brand">
                Planos
              </CustomButton>
            </HStack>
          }
          queryFn={findAllRecurrencesClients}
          queryKey="findAllRecurrencesClients"
          actions={actions}
        />
      </SimpleGrid>
    </Box>
  );
}
